import { IllustrationTree } from '@/components/IllustrationTree';
import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { ROUTES } from '@/constants/routes';
import useGoogleLogin from '@/hooks/useGoogleLogin';
import { SignUpFormData } from '@/types/auth';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatError } from '@treyd-io/core/utils/formik';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import EyeIcon from '@treyd-io/treyd-ui/icons/EyeIcon';
import EyeOffIcon from '@treyd-io/treyd-ui/icons/EyeOffIcon';
import GoogleIcon from '@treyd-io/treyd-ui/icons/GoogleIcon';
import { FormikProps } from 'formik';
import Link from 'next/link';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyrightDisclaimer } from './CopyrightDisclaimer';

interface SignupStepProps {
  handlePreviousStep: () => void;
  onNext: boolean;
  form: FormikProps<SignUpFormData>;
}

export const SignupStep = ({
  handlePreviousStep,
  onNext,
  form,
}: SignupStepProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const { mutate: googleSignIn, isPending: googleLoading } = useGoogleLogin();

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const isLoading = googleLoading || form.isSubmitting;

  return (
    <WizardAnimationWrapper onNext={onNext}>
      <StepLayout
        onMovingBackward={handlePreviousStep}
        containerProps={{
          gap: 1,
        }}>
        <Row>
          <Col xs={12} md>
            <Stack gap={3} width="100%">
              <Stack gap={1}>
                <Typography variant="h4">{t('signup')}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {t(
                    'sign_up_now_to_start_managing_your_business_effortlessly'
                  )}
                </Typography>
              </Stack>
              <Stack gap={2.5} maxWidth={400}>
                <Button
                  color="secondary"
                  variant="outlined"
                  size="large"
                  fullWidth
                  type="button"
                  startIcon={<GoogleIcon />}
                  onClick={() => googleSignIn()}
                  disabled={isLoading}>
                  {t('continueWithGoogle')}
                </Button>
                <Divider>
                  <Typography variant="body2" color="text.secondary">
                    {t('or')}
                  </Typography>
                </Divider>
                <Stack component={'form'} gap={2} onSubmit={form.handleSubmit}>
                  <Stack gap={3}>
                    <Row rowSpacing={3}>
                      <Col xs={6} sm={6}>
                        <TextField
                          fullWidth
                          id="firstName"
                          name="firstName"
                          label={t('first_name')}
                          type={'string'}
                          value={form.values.firstName}
                          onChange={form.handleChange}
                          error={
                            form.touched.firstName &&
                            Boolean(form.errors.firstName)
                          }
                          helperText={
                            form.touched.firstName && form.errors.firstName
                          }
                        />
                      </Col>
                      <Col xs={6} sm={6}>
                        <TextField
                          fullWidth
                          id="lastName"
                          name="lastName"
                          label={t('last_name')}
                          type={'string'}
                          value={form.values.lastName}
                          onChange={form.handleChange}
                          error={
                            form.touched.lastName &&
                            Boolean(form.errors.lastName)
                          }
                          helperText={
                            form.touched.lastName && form.errors.lastName
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          label={t('email')}
                          type={'email'}
                          value={form.values.email}
                          onChange={form.handleChange}
                          error={
                            form.touched.email && Boolean(form.errors.email)
                          }
                          helperText={
                            form.touched.email && formatError(form.errors.email)
                              ? formatError(form.errors.email)
                              : t('we_suggest_using_your_work_email')
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <TextField
                          fullWidth
                          id="password"
                          name="password"
                          label={t('password')}
                          type={showPassword ? 'text' : 'password'}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={t('toggle_password_visibility')}
                                    onClick={handleClickShowPassword}
                                    color={
                                      form.touched.password &&
                                      formatError(form.errors.password)
                                        ? 'error'
                                        : undefined
                                    }>
                                    {showPassword ? (
                                      <EyeOffIcon />
                                    ) : (
                                      <EyeIcon />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                          value={form.values.password}
                          onChange={form.handleChange}
                          error={
                            form.touched.password &&
                            Boolean(form.errors.password)
                          }
                          helperText={
                            form.touched.password &&
                            formatError(form.errors.password)
                              ? formatError(form.errors.password)
                              : t(
                                  'your_password_must_be_at_least_8_characters_long_and_include_at_least_one_digit_one_lowercase_letter_and_one_uppercase_letter'
                                )
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <LoadingButton
                          variant="contained"
                          size="large"
                          fullWidth
                          type="submit"
                          disabled={googleLoading}
                          loading={form.isSubmitting}>
                          {t('signup')}
                        </LoadingButton>
                      </Col>
                    </Row>
                  </Stack>
                  <Stack gap={2}>
                    <CopyrightDisclaimer />
                    <Box gap={1}>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        display="inline"
                        marginRight={1}>
                        {t('account.exists')}
                      </Typography>
                      <Typography
                        component={Link}
                        href={ROUTES.login}
                        variant="body1"
                        color="info.main"
                        display="inline">
                        {t('login')}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Col>
          <Col xs display={{ xs: 'none', md: 'unset' }}>
            <Stack justifyContent="center" alignItems="end" height="100%">
              <IllustrationTree
                style={{ fill: 'none' }}
                sx={{
                  color: 'grey.900',
                  width: 280,
                  height: 391,
                }}
                viewBox="0 0 280 391"
              />
            </Stack>
          </Col>
        </Row>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};
